import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { supabase } from '../utils/supabase/client';
import { API_BASE } from '../utils/supabase/info';
import { fetchWithAuth } from '../utils/api';
import { devError, devLog } from '../utils/logger';
import { useAuth } from './AuthContext';

type ChatContextValue = {
  unreadCount: number;
  refreshUnreadCount: (reason?: string) => void;
};

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

export function ChatProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const unreadRefreshTimerRef = useRef<number | null>(null);
  const unreadFallbackIntervalRef = useRef<number | null>(null);
  const lastUnreadRefreshAtRef = useRef(0);
  const sessionRef = useRef<any>(session);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const scheduleUnreadRefresh = useCallback(
    (
      reason: string,
      timerRef: MutableRefObject<number | null>,
      lastRefreshAtRef: MutableRefObject<number>
    ) => {
      const now = Date.now();
      const elapsed = now - lastRefreshAtRef.current;
      const MIN_REFRESH_GAP_MS = 500;
      // When user opens messages, always refresh badge so it updates immediately when they read
      if (reason !== 'messages:opened' && elapsed < MIN_REFRESH_GAP_MS) return;

      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      timerRef.current = window.setTimeout(async () => {
        try {
          lastRefreshAtRef.current = Date.now();
          devLog('🔄 ChatContext unread refresh:', reason);
          const response = await fetchWithAuth(`${API_BASE}/unread-count`, { cache: 'no-store' });
          if (!response.ok) return;

          const data = await response.json();
          setUnreadCount(data.unreadCount || 0);
        } catch (error) {
          devError('ChatContext unread refresh failed:', error);
        } finally {
          timerRef.current = null;
        }
      }, 150);
    },
    []
  );

  const refreshUnreadCount = useCallback(
    (reason: string = 'manual') => {
      if (!sessionRef.current) {
        setUnreadCount(0);
        return;
      }

      if (reason === 'messages:opened') {
        // Force bypass of min-gap guard so unread badge updates immediately after opening chat.
        lastUnreadRefreshAtRef.current = 0;
      }
      scheduleUnreadRefresh(reason, unreadRefreshTimerRef, lastUnreadRefreshAtRef);
    },
    [scheduleUnreadRefresh]
  );

  useEffect(() => {
    if (!session?.user?.id) {
      setUnreadCount(0);
      return;
    }

    const userId = session.user.id as string;
    refreshUnreadCount('session:start');

    const startUnreadFallback = () => {
      if (unreadFallbackIntervalRef.current !== null) return;
      devLog('⚠️ Chat unread realtime degraded - starting controlled fallback polling');
      unreadFallbackIntervalRef.current = window.setInterval(() => {
        refreshUnreadCount('fallback:interval');
      }, 45000);
    };

    const stopUnreadFallback = () => {
      if (unreadFallbackIntervalRef.current === null) return;
      window.clearInterval(unreadFallbackIntervalRef.current);
      unreadFallbackIntervalRef.current = null;
      devLog('✅ Chat unread realtime recovered - stopping fallback polling');
    };

    const unreadChannel = supabase
      .channel(`chat-unread:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${userId}`,
        },
        () => {
          refreshUnreadCount('messages:*');
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          devLog('✅ ChatContext unread channel subscribed');
          stopUnreadFallback();
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          devError('❌ ChatContext unread channel error:', status);
          startUnreadFallback();
        }
      });

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        refreshUnreadCount('app:visible');
      }
    };

    const handleOnline = () => {
      refreshUnreadCount('network:online');
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('online', handleOnline);

    return () => {
      stopUnreadFallback();
      supabase.removeChannel(unreadChannel);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      window.removeEventListener('online', handleOnline);

      if (unreadRefreshTimerRef.current !== null) {
        window.clearTimeout(unreadRefreshTimerRef.current);
        unreadRefreshTimerRef.current = null;
      }
    };
  }, [session?.user?.id, refreshUnreadCount]);

  return (
    <ChatContext.Provider
      value={{
        unreadCount,
        refreshUnreadCount,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
